import { categoryGroupKeyForLabel } from "../editor/model_detail"
import { CategoricalTileGrid } from "../modelling/tile_grid"
import type { DatasetLayer, ModelOutputLayer } from "../state"

export type RgbaColor = [number, number, number, number]
export type ColorByLabel = Record<string, RgbaColor>

/** Style fields on model/dataset map layers that drive categorical colouring. */
export type CategoricalLayerColorStyle = {
  colors?: RgbaColor[]
  colorByLabel?: ColorByLabel
}

export type CategoricalLabelEntry = {
  key: number
  label: string
}

type StatsLabelRow = { name: number; value: string }

const FALLBACK_COLOR: RgbaColor = [0, 0, 0, 0]

export function usesLabelBasedColors(style: CategoricalLayerColorStyle): boolean {
  const map = style.colorByLabel
  return map != null && Object.keys(map).length > 0
}

export function labelEntriesFromGrid(grid: CategoricalTileGrid): CategoricalLabelEntry[] {
  return Array.from(grid.labels?.entries() ?? [])
    .filter(([key]) => key !== 255)
    .sort((a, b) => a[0] - b[0])
    .map(([key, label]) => ({ key, label: String(label) }))
}

export function labelEntriesFromStatsLabels(statsLabels: StatsLabelRow[] | undefined): CategoricalLabelEntry[] {
  if (!statsLabels) return []
  return statsLabels
    .filter(row => Number(row.name) !== 255)
    .map(row => ({ key: Number(row.name), label: String(row.value) }))
}

export function categoricalStyleCacheKey(style: CategoricalLayerColorStyle): string {
  return JSON.stringify({
    colors: style.colors ?? null,
    colorByLabel: style.colorByLabel ?? null,
  })
}

/**
 * Resolve the colour for one category. Label-based map is used only when
 * `colorByLabel` is present (new saves / after user migration). Otherwise, or
 * when the label was renamed and no longer matches, falls back to index slot.
 */
export function resolveCategoryColor(
  key: number,
  label: string | undefined,
  style: CategoricalLayerColorStyle,
  fallback: RgbaColor = FALLBACK_COLOR
): RgbaColor {
  if (usesLabelBasedColors(style) && label) {
    const byLabel = style.colorByLabel![categoryGroupKeyForLabel(label)]
    if (byLabel && byLabel.length >= 4) {
      return [Number(byLabel[0]), Number(byLabel[1]), Number(byLabel[2]), Number(byLabel[3])]
    }
  }

  const indexed = style.colors?.[key - 1]
  if (indexed && indexed.length >= 4) {
    return [Number(indexed[0]), Number(indexed[1]), Number(indexed[2]), Number(indexed[3])]
  }

  return fallback
}

/** Build a maxKey-length palette for WebGL / legend / export. */
export function resolveCategoricalPalette(
  labels: CategoricalLabelEntry[],
  maxKey: number,
  style: CategoricalLayerColorStyle,
  fillMissing?: (index: number) => RgbaColor
): RgbaColor[] {
  if (maxKey <= 0) return []

  const labelByKey = new Map<number, string>()
  labels.forEach(({ key, label }) => labelByKey.set(key, label))

  const palette: RgbaColor[] = []
  for (let key = 1; key <= maxKey; key++) {
    const fallback = fillMissing ? fillMissing(key - 1) : FALLBACK_COLOR
    palette.push(resolveCategoryColor(key, labelByKey.get(key), style, fallback))
  }
  return palette
}

export function resolvePaletteForCategoricalLayer(
  grid: CategoricalTileGrid,
  layer: Pick<ModelOutputLayer | DatasetLayer, "colors" | "colorByLabel">,
  fillMissing?: (index: number) => RgbaColor
): RgbaColor[] {
  const labels = labelEntriesFromGrid(grid)
  const maxKey = grid.getMinMax()[1]
  return resolveCategoricalPalette(labels, maxKey, layer, fillMissing)
}

/** User legend edits: migrate legacy index palettes to label-keyed on first edit. */
export function applyCategoricalColorMutation(
  labels: CategoricalLabelEntry[],
  maxKey: number,
  updatedPalette: RgbaColor[],
  style: CategoricalLayerColorStyle,
  migrateToLabels = true
): CategoricalLayerColorStyle {
  const colors: RgbaColor[] = []
  for (let i = 0; i < maxKey; i++) {
    const source = updatedPalette[i] ?? style.colors?.[i] ?? FALLBACK_COLOR
    colors.push([
      Number(source[0]),
      Number(source[1]),
      Number(source[2]),
      Number(source[3] ?? 1),
    ])
  }

  if (!migrateToLabels && !usesLabelBasedColors(style)) {
    return { colors }
  }

  const colorByLabel: ColorByLabel = { ...(style.colorByLabel ?? {}) }
  labels.forEach(({ key, label }) => {
    const color = colors[key - 1]
    if (color) {
      colorByLabel[categoryGroupKeyForLabel(label)] = [color[0], color[1], color[2], color[3]]
    }
  })

  return { colors, colorByLabel }
}

/** Resolved source colours keyed by normalized label string. */
function sourceColorsByLabel(
  sourceLabels: CategoricalLabelEntry[],
  sourceStyle: CategoricalLayerColorStyle
): ColorByLabel {
  const sourceMaxKey = sourceLabels.reduce((max, entry) => Math.max(max, entry.key), 0)
  const sourcePalette = resolveCategoricalPalette(sourceLabels, sourceMaxKey, sourceStyle)
  const byLabel: ColorByLabel = {}

  sourceLabels.forEach(({ key, label }) => {
    const color = sourcePalette[key - 1]
    if (color) {
      byLabel[categoryGroupKeyForLabel(label)] = [
        Number(color[0]),
        Number(color[1]),
        Number(color[2]),
        Number(color[3] ?? 1),
      ]
    }
  })

  return byLabel
}

/**
 * Label-aware style copy between two categorical layers. Colours are matched
 * by category label string, not numeric class index. Unmatched target labels
 * (e.g. renamed classes) fall back to the source colour at the same index.
 */
export function remappedCategoricalStyleForCopy(
  sourceLabels: CategoricalLabelEntry[],
  sourceStyle: CategoricalLayerColorStyle,
  targetLabels: CategoricalLabelEntry[],
  targetMaxKey: number
): CategoricalLayerColorStyle {
  const sourceMaxKey = sourceLabels.reduce((max, entry) => Math.max(max, entry.key), 0)
  const sourcePalette = resolveCategoricalPalette(sourceLabels, sourceMaxKey, sourceStyle)
  const sourceByLabel = sourceColorsByLabel(sourceLabels, sourceStyle)
  const colorByLabel: ColorByLabel = {}

  targetLabels.forEach(({ key, label }) => {
    const labelKey = categoryGroupKeyForLabel(label)
    const matched = sourceByLabel[labelKey]
    if (matched) {
      colorByLabel[labelKey] = [...matched]
      return
    }

    // Renamed / new class: fall back to source index slot when present.
    const indexed = sourcePalette[key - 1]
    if (indexed) {
      colorByLabel[labelKey] = [
        Number(indexed[0]),
        Number(indexed[1]),
        Number(indexed[2]),
        Number(indexed[3] ?? 1),
      ]
    }
  })

  const colors = resolveCategoricalPalette(
    targetLabels,
    targetMaxKey,
    { colorByLabel },
    () => [128, 128, 128, 1]
  )

  return { colors, colorByLabel }
}

export function mergeCategoricalStyleOnCopy(
  extractedStyle: Partial<ModelOutputLayer | DatasetLayer>,
  sourceLayer: ModelOutputLayer | DatasetLayer,
  sourceLabels: CategoricalLabelEntry[],
  targetLabels: CategoricalLabelEntry[],
  targetMaxKey: number
): Partial<ModelOutputLayer | DatasetLayer> {
  if (targetMaxKey <= 0 || targetLabels.length === 0) {
    return extractedStyle
  }

  const remapped = remappedCategoricalStyleForCopy(
    sourceLabels,
    { colors: sourceLayer.colors, colorByLabel: sourceLayer.colorByLabel },
    targetLabels,
    targetMaxKey
  )

  return {
    ...extractedStyle,
    colors: remapped.colors,
    colorByLabel: remapped.colorByLabel,
  }
}
